import { useState } from "react";
import {
  ActionIcon,
  Alert,
  Avatar,
  Badge,
  Grid,
  Group,
  Modal,
  Paper,
  Progress,
  ScrollArea,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAlertCircle,
  IconCheck,
  IconClock,
  IconEye,
} from "@tabler/icons-react";
import dayjs from "dayjs";

import {
  EngagementFilters,
  useEngagementDashboard,
} from "@hooks/queries/accountManagement/useAccountManagement";

import { EngagementAccount } from "@customTypes/accountManagement";


const formatDuration = (seconds: number) => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  return `${minutes} min`;
};

const Metric = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: string | number;
  hint?: string;
}) => (
  <Paper withBorder radius="md" p="md">
    <Text size="xs" c="dimmed">
      {label}
    </Text>
    <Text size="xl" fw={700} c="indigo.9">
      {value}
    </Text>
    {hint && (
      <Text size="xs" c="dimmed">
        {hint}
      </Text>
    )}
  </Paper>
);

const featureLabels: Record<string, string> = {
  profile_visited: "Profile visited",
  booking_link_shared: "Booking link shared",
  services: "Consultations / services",
  withdrawals: "Payout requested",
  doctor_referrals: "Doctor referrals",
};

const activationLabels: Record<string, string> = {
  profile_live: "Profile verified and live",
  booking_link_shared: "Booking link shared",
  profile_visited: "Attributed profile visitor",
  first_service_completed: "First service completed",
  first_payout_requested: "First payout requested",
};

export const ActivationEngagementTab = () => {
  const [filters, setFilters] = useState<EngagementFilters>({
    dateRange: "30d",
    accountType: "",
  });
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<EngagementAccount | null>(null);
  const [opened, modal] = useDisclosure(false);
  const query = useEngagementDashboard(filters);
  const data = query.data;
  const accounts = (data?.accounts || []).filter(account =>
    account.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Stack gap="lg" pb={120}>
      <Group justify="space-between" align="end">
        <div>
          <Title order={3}>Activation & Engagement</Title>
          <Text size="sm" c="dimmed">
            Post-live adoption for doctors and organizations.
          </Text>
        </div>
        <Group align="end">
          <Select
            label="Period"
            data={[
              { value: "7d", label: "Last 7 days" },
              { value: "30d", label: "Last 30 days" },
              { value: "90d", label: "Last 90 days" },
              { value: "all", label: "All time" },
              { value: "custom", label: "Custom" },
            ]}
            value={filters.dateRange}
            onChange={value =>
              setFilters(current => ({
                ...current,
                dateRange: (value || "30d") as EngagementFilters["dateRange"],
              }))
            }
          />
          <Select
            label="Account type"
            clearable
            data={[
              { value: "doctor", label: "Doctors" },
              { value: "organization", label: "Organizations" },
            ]}
            value={filters.accountType || null}
            onChange={value =>
              setFilters(current => ({
                ...current,
                accountType: (value || "") as EngagementFilters["accountType"],
              }))
            }
          />
        </Group>
      </Group>

      {filters.dateRange === "custom" && (
        <Group>
          <TextInput
            type="datetime-local"
            label="From"
            value={filters.startDate || ""}
            onChange={event =>
              setFilters(current => ({
                ...current,
                startDate: event.currentTarget.value,
              }))
            }
          />
          <TextInput
            type="datetime-local"
            label="To"
            value={filters.endDate || ""}
            onChange={event =>
              setFilters(current => ({
                ...current,
                endDate: event.currentTarget.value,
              }))
            }
          />
        </Group>
      )}

      <Alert
        variant="light"
        color="blue"
        icon={<IconAlertCircle size={18} />}>
        Engagement analytics are forward-only
        {data?.coverage.tracking_started_at
          ? ` from ${dayjs(data.coverage.tracking_started_at).format(
              "D MMM YYYY"
            )}.`
          : ". Coverage begins when first-party tracking is deployed."}
      </Alert>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }}>
        <Metric
          label="Activated Accounts"
          value={data?.summary.activated_accounts || 0}
          hint={`${data?.summary.activated_doctors || 0} doctors · ${
            data?.summary.activated_organizations || 0
          } organizations`}
        />
        <Metric
          label="Avg. Feature Adoption"
          value={`${data?.summary.average_feature_adoption_percentage || 0}%`}
        />
        <Metric
          label="Time to First Service"
          value={
            data?.summary.average_time_to_first_service_days != null
              ? `${data.summary.average_time_to_first_service_days} days`
              : "—"
          }
        />
        <Metric
          label="Time to First Paid"
          value={
            data?.summary.average_time_to_first_payment_days != null
              ? `${data.summary.average_time_to_first_payment_days} days`
              : "—"
          }
        />
        <Metric
          label="Avg. Profile Engagement"
          value={formatDuration(
            data?.summary.average_profile_engagement_seconds || 0
          )}
        />
      </SimpleGrid>

      <Paper withBorder radius="md" p="md">
        <Title order={4}>Feature Adoption</Title>
        <Text size="xs" c="dimmed" mb="lg">
          Adoption among eligible live accounts
        </Text>
        <Grid>
          {Object.entries(data?.feature_adoption || {}).map(
            ([feature, adoption]) => (
              <Grid.Col key={feature} span={{ base: 12, sm: 6, lg: 4 }}>
                <Paper withBorder p="md" radius="md">
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={600}>
                      {featureLabels[feature] || feature}
                    </Text>
                    <Text fw={700}>{adoption.percentage}%</Text>
                  </Group>
                  <Progress value={adoption.percentage} color="violet" />
                  <Text size="xs" c="dimmed" mt="xs">
                    {adoption.accounts} of {data?.summary.live_accounts || 0}{" "}
                    accounts
                  </Text>
                </Paper>
              </Grid.Col>
            )
          )}
        </Grid>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Title order={4} mb="md">
          Sharing Attribution Funnel
        </Title>
        <SimpleGrid cols={{ base: 2, md: 4 }}>
          {[
            ["Share intents", data?.attribution_funnel.share_intents || 0],
            [
              "Unique visitors",
              data?.attribution_funnel.unique_visitors || 0,
            ],
            ["Bookings", data?.attribution_funnel.bookings || 0],
            ["Successful payments", data?.attribution_funnel.payments || 0],
          ].map(([label, value], index) => (
            <Paper key={String(label)} bg="violet.0" p="md" radius="md">
              <Text size="xs" c="dimmed">
                {index + 1}. {label}
              </Text>
              <Text size="xl" fw={700}>
                {value}
              </Text>
            </Paper>
          ))}
        </SimpleGrid>
      </Paper>

      <Paper withBorder radius="md" p="md">
        <Group justify="space-between" mb="md">
          <div>
            <Title order={4}>Account Engagement</Title>
            <Text size="xs" c="dimmed">
              Feature adoption per live account
            </Text>
          </div>
          <TextInput
            placeholder="Search an account"
            value={search}
            onChange={event => setSearch(event.currentTarget.value)}
          />
        </Group>
        <ScrollArea>
          <Table miw={1200} highlightOnHover>
            <Table.Thead bg="gray.0">
              <Table.Tr>
                <Table.Th>Account</Table.Th>
                <Table.Th>Visitors</Table.Th>
                <Table.Th>Payouts</Table.Th>
                <Table.Th>Referrals</Table.Th>
                <Table.Th>Services</Table.Th>
                <Table.Th>Sessions</Table.Th>
                <Table.Th>Avg. Engagement</Table.Th>
                <Table.Th>Last Session</Table.Th>
                <Table.Th>Last Service</Table.Th>
                <Table.Th>Activation</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {query.isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={11}>
                    <Text ta="center" c="dimmed" py="xl">
                      Loading engagement…
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : query.isError ? (
                <Table.Tr>
                  <Table.Td colSpan={11}>
                    <Text ta="center" c="red" py="xl">
                      Engagement data could not be loaded.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : accounts.length ? (
                accounts.map(account => (
                  <Table.Tr key={`${account.account_type}-${account.account_id}`}>
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        <Avatar size={30} color="violet">
                          {account.name.slice(0, 2).toUpperCase()}
                        </Avatar>
                        <div>
                          <Text size="sm" fw={500}>
                            {account.name}
                          </Text>
                          <Text size="xs" c="dimmed" tt="capitalize">
                            {account.account_type}
                          </Text>
                        </div>
                      </Group>
                    </Table.Td>
                    <Table.Td>{account.attributed_visitors}</Table.Td>
                    <Table.Td>{account.payout_requests}</Table.Td>
                    <Table.Td>{account.referrals}</Table.Td>
                    <Table.Td>{account.services}</Table.Td>
                    <Table.Td>{account.sessions}</Table.Td>
                    <Table.Td>
                      {formatDuration(account.average_engagement_seconds)}
                    </Table.Td>
                    <Table.Td>
                      {account.last_session_at
                        ? dayjs(account.last_session_at).format("D MMM, h:mm A")
                        : "—"}
                    </Table.Td>
                    <Table.Td>
                      {account.last_service_at
                        ? dayjs(account.last_service_at).format("D MMM YYYY")
                        : "—"}
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={
                          account.activation_status === "activated"
                            ? "green"
                            : "orange"
                        }
                        variant="light">
                        {account.activation_status.replace("_", " ")}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        aria-label="View activation details"
                        onClick={() => {
                          setSelected(account);
                          modal.open();
                        }}>
                        <IconEye size={17} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))
              ) : (
                <Table.Tr>
                  <Table.Td colSpan={11}>
                    <Text ta="center" c="dimmed" py="xl">
                      No live accounts match these filters.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>

      <Modal
        opened={opened}
        onClose={modal.close}
        title="Activation details"
        centered>
        {selected && (
          <Stack>
            <div>
              <Title order={4}>{selected.name}</Title>
              <Text size="sm" c="dimmed" tt="capitalize">
                {selected.account_type}
              </Text>
            </div>
            {Object.entries(selected.activation_steps).map(
              ([milestone, complete]) => (
                <Group key={milestone} justify="space-between">
                  <Group gap="sm">
                    <ThemeIcon
                      variant="light"
                      color={complete ? "green" : "gray"}
                      radius="xl">
                      {complete ? (
                        <IconCheck size={16} />
                      ) : (
                        <IconClock size={16} />
                      )}
                    </ThemeIcon>
                    <Text size="sm">
                      {activationLabels[milestone] || milestone}
                    </Text>
                  </Group>
                  <Badge color={complete ? "green" : "gray"} variant="light">
                    {complete ? "Complete" : "Pending"}
                  </Badge>
                </Group>
              )
            )}
          </Stack>
        )}
      </Modal>
    </Stack>
  );
};
